import React, { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Film, Music, Image as ImageIcon, Upload, Loader2, Volume2, VolumeX, X, Sparkles, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MusicPicker from "../components/MusicPicker";
import ImportSocialReelModal from "../components/ImportSocialReelModal";

/**
 * ReelComposer — Instagram/TikTok-style vertical reel creation flow.
 * Upload a 9:16 video from device (or record one later), optionally attach
 * a 30-sec music preview, write caption + pick category, hit Publish.
 * The reel is persisted via /api/videos with format="story" so it shows
 * up in the existing Shorts feed + Stories rail.
 */
export default function ReelComposer() {
  const nav = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();
  const fileRef = useRef(null);
  const thumbRef = useRef(null);
  const videoRef = useRef(null);
  const previewRef = useRef(null);

  const [videoFile, setVideoFile] = useState(null);
  const [videoLocalUrl, setVideoLocalUrl] = useState("");
  const [videoStoragePath, setVideoStoragePath] = useState("");
  const [videoRemoteUrl, setVideoRemoteUrl] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [music, setMusic] = useState(null);
  const [musicOpen, setMusicOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [title, setTitle] = useState("");

  // Deep-link: /reels/create?import=1 (or #import) auto-opens the modal so
  // the Header / dashboard button can land users straight on the import flow.
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    if (params.get("import") === "1" || loc.hash === "#import") setImportOpen(true);
  }, [loc.search, loc.hash]);
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("shorts");
  const [muted, setMuted] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const canPublish = !!title.trim() && (!!videoStoragePath || !!videoRemoteUrl) && !publishing;

  const canCreate = user && ["creator", "premium_creator", "admin"].includes(user.role);

  const pickVideo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("video/")) return toast.error("Please choose a video file");
    if (f.size > 200 * 1024 * 1024) return toast.error("Video too large (max 200MB)");
    setVideoFile(f);
    const local = URL.createObjectURL(f);
    setVideoLocalUrl(local);
    // Upload in background
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/uploads/video", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setVideoStoragePath(data.storage_path);
      setVideoRemoteUrl(`/api/files/${data.storage_path}`);
      toast.success("Video uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const pickThumb = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) return toast.error("Image too large (max 20MB)");
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setThumbnail(`/api/files/${data.storage_path}`);
      toast.success("Cover image set");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onVideoLoaded = () => {
    // Auto-mute primary video if music is attached so audio doesn't double.
    const v = videoRef.current;
    if (v) v.muted = muted || !!music;
  };

  const toggleMute = () => {
    const v = videoRef.current;
    const p = previewRef.current;
    const next = !muted;
    setMuted(next);
    if (v) v.muted = next || !!music;
    if (p) p.muted = next;
  };

  const publish = async () => {
    if (!canPublish) return;
    setPublishing(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim(),
        category,
        is_short: true,
        format: "story",
        aspect_ratio: "9:16",
        storage_path: videoStoragePath || undefined,
        video_url: videoStoragePath ? undefined : videoRemoteUrl,
        thumbnail_url: thumbnail || undefined,
        music: music || undefined,
        tags: ["reel"],
      };
      const { data } = await api.post("/videos", body);
      toast.success("Reel published");
      nav(`/watch/${data.id}`);
    } catch (err) {
      const msg = formatApiError(err);
      if (/waiver/i.test(msg)) toast.error("Please sign the creator waiver first (Studio → Upload)");
      else toast.error(msg);
    } finally { setPublishing(false); }
  };

  if (!canCreate) {
    return (
      <div className="pt-32 pb-24 px-6 max-w-xl mx-auto text-center text-white" data-testid="reel-gate">
        <Film className="w-12 h-12 mx-auto text-tv-accent" />
        <h1 className="font-display font-black text-4xl mt-4">Create a Reel</h1>
        <p className="text-white/70 mt-3">Reels are for approved creators. Apply for a creator account to start publishing vertical videos with music.</p>
        <button onClick={() => nav("/apply-creator")} className="mt-6 bg-tv-accent hover:bg-yellow-400 text-black font-bold px-6 py-3 rounded-full" data-testid="reel-apply-btn">Apply as creator</button>
      </div>
    );
  }

  return (
    <div data-testid="reel-composer" className="pt-28 pb-24 px-4 sm:px-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3">
        <Film className="w-5 h-5 text-tv-accent" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-bold">Create Reel</div>
      </div>
      <h1 className="font-display font-black text-4xl sm:text-5xl text-white mt-2 tracking-tight">Make a reel</h1>
      <p className="text-white/60 mt-1 text-sm">Upload a vertical clip from your phone or camera roll, attach a track, publish in seconds.</p>

      {/* Import-from-social shortcut — paste a TikTok/IG/YouTube/X/FB/Vimeo
          URL and we'll re-host it on Tap IN so it plays on TVs too. */}
      <div className="mt-6 rounded-2xl border border-white/10 bg-gradient-to-r from-fuchsia-500/10 via-rose-500/10 to-amber-500/10 p-4 flex items-center gap-4">
        <div className="hidden sm:block shrink-0 w-10 h-10 rounded-full bg-tv-accent/20 flex items-center justify-center">
          <LinkIcon className="w-5 h-5 text-tv-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-white text-sm">Already posted it on TikTok or Instagram?</div>
          <div className="text-white/60 text-xs">Paste the link and we'll re-host the clip — plays on TV too.</div>
        </div>
        <button
          type="button"
          data-testid="reel-import-social-btn"
          onClick={() => setImportOpen(true)}
          className="shrink-0 bg-white text-black font-bold text-sm px-4 py-2 rounded-full inline-flex items-center gap-2 hover:bg-white/90"
        >
          <LinkIcon className="w-4 h-4" /> Import from social
        </button>
      </div>

      <div className="grid md:grid-cols-[360px_1fr] gap-8 mt-8">
        {/* Preview column */}
        <div>
          <div className="relative aspect-[9/16] w-full max-w-[360px] rounded-2xl overflow-hidden bg-black border border-white/10 mx-auto">
            {videoLocalUrl || videoRemoteUrl ? (
              <video
                ref={videoRef}
                src={videoLocalUrl || videoRemoteUrl}
                autoPlay
                loop
                muted={muted || !!music}
                playsInline
                onLoadedData={onVideoLoaded}
                className="w-full h-full object-cover"
                data-testid="reel-preview-video"
              />
            ) : (
              <button
                onClick={() => fileRef.current?.click()}
                data-testid="reel-pick-video"
                className="w-full h-full flex flex-col items-center justify-center gap-3 text-white/70 hover:text-white bg-gradient-to-b from-white/5 to-white/0"
              >
                <Upload className="w-8 h-8" />
                <div className="font-semibold">Tap to upload</div>
                <div className="text-xs text-white/50">MP4 · MOV · WebM · up to 200MB</div>
              </button>
            )}
            {uploading && (
              <div className="absolute top-2 left-2 right-2 flex items-center gap-2 bg-black/70 text-white text-xs rounded-full px-3 py-1.5" data-testid="reel-uploading">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading video…
              </div>
            )}
            {music && (
              <div className="absolute bottom-3 left-3 right-3 bg-black/60 backdrop-blur rounded-full px-3 py-1.5 flex items-center gap-2" data-testid="reel-music-badge">
                {music.album_art && <img src={music.album_art} alt="" className="w-6 h-6 rounded object-cover" />}
                <div className="min-w-0 flex-1">
                  <div className="text-white text-[11px] font-semibold truncate">{music.title}</div>
                  <div className="text-white/60 text-[10px] truncate">{music.artist}</div>
                </div>
                <Music className="w-3.5 h-3.5 text-tv-accent" />
              </div>
            )}
            {(videoLocalUrl || videoRemoteUrl) && (
              <button onClick={toggleMute} className="absolute top-3 right-3 bg-black/60 hover:bg-black/80 text-white rounded-full p-2" data-testid="reel-mute-toggle">
                {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
            )}
          </div>
          {music?.preview_url && (
            <audio ref={previewRef} src={music.preview_url} autoPlay loop muted={muted} data-testid="reel-music-audio" />
          )}
          <input ref={fileRef} type="file" accept="video/*" capture="user" onChange={pickVideo} className="hidden" />
        </div>

        {/* Editor column */}
        <div className="space-y-4">
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider font-semibold">Caption</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={140}
              placeholder="Say something catchy…"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-accent"
              data-testid="reel-title-input"
            />
          </div>
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider font-semibold">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Add hashtags, credits, context…"
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-tv-accent resize-none"
              data-testid="reel-desc-input"
            />
          </div>
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider font-semibold">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white outline-none focus:border-tv-accent"
              data-testid="reel-category-select"
            >
              <option value="shorts">Shorts</option>
              <option value="music">Music</option>
              <option value="films">Films</option>
              <option value="cooking">Cooking</option>
              <option value="fitness">Fitness</option>
              <option value="gaming">Gaming</option>
              <option value="podcasts">Podcasts</option>
              <option value="news">News</option>
              <option value="gospel">Gospel</option>
              <option value="kids">Kids</option>
            </select>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <button
              type="button"
              onClick={() => setMusicOpen(true)}
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm px-4 py-2 rounded-full"
              data-testid="reel-add-music-btn"
            >
              <Music className="w-4 h-4" /> {music ? "Change music" : "Add music"}
            </button>
            {music && (
              <button type="button" onClick={() => setMusic(null)} className="text-xs text-white/50 hover:text-white flex items-center gap-1" data-testid="reel-remove-music">
                <X className="w-3 h-3" /> Remove
              </button>
            )}
            <button
              type="button"
              onClick={() => thumbRef.current?.click()}
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm px-4 py-2 rounded-full"
              data-testid="reel-thumb-btn"
            >
              <ImageIcon className="w-4 h-4" /> {thumbnail ? "Change cover" : "Add cover"}
            </button>
            <input ref={thumbRef} type="file" accept="image/*" onChange={pickThumb} className="hidden" />
            {thumbnail && <img src={thumbnail} alt="" className="w-8 h-8 rounded object-cover border border-white/20" />}
          </div>

          <div className="pt-4 border-t border-white/10 flex flex-wrap items-center gap-3">
            <button
              onClick={publish}
              disabled={!canPublish}
              className="bg-tv-accent hover:bg-yellow-400 disabled:opacity-40 text-black font-bold px-6 py-3 rounded-full flex items-center gap-2"
              data-testid="reel-publish-btn"
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {publishing ? "Publishing…" : "Publish reel"}
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              className="text-white/70 hover:text-white text-sm font-semibold"
              data-testid="reel-replace-video-btn"
            >
              {videoLocalUrl ? "Replace video" : "Choose video"}
            </button>
          </div>
        </div>
      </div>

      <MusicPicker open={musicOpen} onClose={() => setMusicOpen(false)} onSelect={setMusic} />
      <ImportSocialReelModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={({ video_id }) => { if (video_id) nav(`/watch/${video_id}`); }}
      />
    </div>
  );
}
